import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
import { getSettingUsersAction } from "../../../store/actions/SettingAction";
import paymentGatewayService from "../../../services/PaymentGatewayService";
import SendPaymentLinkButton from "./SendPaymentLinkButton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CommonPaymentModal = ({
  show,
  onHide,
  title = "Collect Payment",
  patientName = "",
  initialAmount = "",
  paymentModes = [
    { value: "Online", label: "Online" },
    { value: "Bankingcard", label: "Banking Card" },
    { value: "Upi", label: "UPI" },
    { value: "Offline", label: "Cash" },
  ],
  enableCoupons = true,
  onSubmit,
  onCancel,
  loading = false,
  submitButtonText = "Collect Payment",
  cancelButtonText = "Cancel",
  showPatientName = true,
  requiredFields = ["amount", "paymentType"],
  customValidation = null,
  additionalFields = [],
  onAmountChange = null,
  onPaymentTypeChange = null,
  onCouponChange = null,
  // New props for different payment types
  paymentType = "appointment", // "appointment", "medicine", "receptionist"
  paymentData = {}, // Additional data needed for specific payment types
  referenceId = "", // For medicine payments
  paymentFor = "APPOINTMENT", // "APPOINTMENT", "MEDICINE"
}) => {
  const [formData, setFormData] = useState({
    amount: initialAmount || "",
    paymentType: "Online",
    couponCode: "",
    finalAmount: null,
    additionalFieldValues: {},
  });

  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const couponInputRef = useRef();
  const [consultingFee, setConsultingFee] = useState("");

  useEffect(() => {
    if (paymentType === "appointment" || paymentType === "receptionist") {
      const fetchSettings = async () => {
        try {
          const settings = await dispatch(getSettingUsersAction());
          const consulting = settings.find(
            (s) => s.setting_name === "Consulting Fees"
          );
          if (consulting) {
            setConsultingFee(consulting.setting_value);
            setFormData((prev) => ({
              ...prev,
              amount: consulting.setting_value,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch consulting fee:", err);
        }
      };
      fetchSettings();
    }
  }, [dispatch, paymentType]);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (show) {
      setFormData({
        amount: consultingFee || "",
        paymentType: "Online",
        couponCode: "",
        finalAmount: null,
        additionalFieldValues: {},
      });
      setErrors({});
    }
  }, [show, consultingFee]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Call custom change handlers if provided
    if (field === "amount" && onAmountChange) {
      onAmountChange(value);
    }
    if (field === "paymentType" && onPaymentTypeChange) {
      onPaymentTypeChange(value);
    }
    if (field === "couponCode" && onCouponChange) {
      onCouponChange(value);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      }
    });

    // Amount validation
    if (formData.amount) {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = "Please enter a valid amount greater than 0";
      }
    }

    // Custom validation
    if (customValidation) {
      const customValidationResult = customValidation(formData);
      if (!customValidationResult.isValid) {
        newErrors.custom = customValidationResult.message;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApplyCoupon = () => {
    const amt = Number.parseFloat(formData.amount);
    if (!amt || amt <= 0) {
      showErrorAlert("Enter a valid amount before applying the coupon.");
      return;
    }

    if (formData.couponCode.trim() === "") {
      showErrorAlert("Please enter a valid coupon code.");
      return;
    }

    // Set final amount to 0 for any coupon code (as per requirements)
    handleInputChange("finalAmount", 0);
    couponInputRef?.current?.blur();
  };

  const handleRemoveCoupon = () => {
    handleInputChange("couponCode", "");
    handleInputChange("finalAmount", null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare payment data based on payment type
      let paymentPayload = {
        amount: parseFloat(formData.amount),
        paymentType: formData.paymentType,
        couponCode: formData.couponCode || null,
        finalAmount:
          formData.finalAmount !== null
            ? formData.finalAmount
            : parseFloat(formData.amount),
        additionalFieldValues: formData.additionalFieldValues,
      };

      // Add payment type specific data
      switch (paymentType) {
        case "appointment":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData, // Include appointment_id, lead_id, patient_id, etc.
            payment_for: paymentFor,
          };
          break;
        case "medicine":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData, // Include queue_id, patient_id, etc.
            payment_for: "MEDICINE",
            reference_id: referenceId,
          };
          break;
        case "receptionist":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData, // Include queue_id, patient_id, etc.
            payment_for: paymentFor,
          };
          break;
        default:
          break;
      }

      await onSubmit(paymentPayload);
    } catch (error) {
      console.error("Payment submission error:", error);
      showErrorAlert("Payment failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onHide();
    }
  };

  const pollPaymentStatus = async (transactionNumber) => {
    const maxAttempts = 30; // 5 minutes
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await paymentGatewayService.checkPaymentStatus(
          transactionNumber
        );

        if (response.success) {
          const status = response.data.status;

          if (status === "SUCCESS") {
            toast.success("Payment completed successfully!");
            await handlePaymentSuccess(response.data);
            return;
          } else if (status === "FAILED") {
            toast.error("Payment failed");
            localStorage.removeItem("pendingPayment");
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        } else {
          toast.info(
            "Payment status check timeout. Please verify payment manually."
          );
        }
      } catch (error) {
        console.error("Status check error:", error);
      }
    };

    setTimeout(checkStatus, 10000);
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      // Prepare payment payload for your existing backend
      let paymentPayload = {
        amount: parseFloat(formData.amount),
        paymentType: "Online",
        couponCode: formData.couponCode || null,
        finalAmount:
          formData.finalAmount !== null
            ? formData.finalAmount
            : parseFloat(formData.amount),
        additionalFieldValues: formData.additionalFieldValues,
        gateway_transaction_id: paymentResponse.transactionNumber,
        gateway_response: paymentResponse,
      };

      // Add payment type specific data
      switch (paymentType) {
        case "appointment":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData,
            payment_for: paymentFor,
          };
          break;
        case "medicine":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData,
            payment_for: "MEDICINE",
            reference_id: referenceId,
          };
          break;
        case "receptionist":
          paymentPayload = {
            ...paymentPayload,
            ...paymentData,
            payment_for: paymentFor,
          };
          break;
        default:
          break;
      }

      // Call your existing onSubmit function
      await onSubmit(paymentPayload);
      localStorage.removeItem("pendingPayment");
    } catch (error) {
      console.error("Payment success handling error:", error);
      showErrorAlert("Failed to process payment. Please contact support.");
    }
  };

  // Generate payment link based on context

  const isCouponApplied = formData.finalAmount !== null;
  const showCouponSection = enableCoupons && formData.paymentType === "Offline";

  return (
    <>
      <Modal
        show={show}
        onHide={handleCancel}
        centered
        size="lg"
        style={{ minWidth: "700px" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
            {showPatientName && patientName && (
              <span className="text-muted ms-2">for {patientName}</span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form onSubmit={handleSubmit}>
            {/* Amount Field */}
            <div className="mb-3">
              <label htmlFor="amount" className="form-label">
                Amount <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                id="amount"
                value={formData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                placeholder="Enter amount"
                min="0.01"
                step="0.01"
                required
                readOnly={
                  paymentType === "appointment" ||
                  paymentType === "receptionist"
                }
                style={
                  paymentType === "appointment" ||
                  paymentType === "receptionist"
                    ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    : {}
                }
              />
              {errors.amount && (
                <div className="invalid-feedback">{errors.amount}</div>
              )}
            </div>

            {/* Payment Type Field */}
            <div className="mb-3">
              <label htmlFor="paymentType" className="form-label">
                Payment Mode <span className="text-danger">*</span>
              </label>
              <select
                className={`form-control ${
                  errors.paymentType ? "is-invalid" : ""
                }`}
                id="paymentType"
                value={formData.paymentType}
                onChange={(e) =>
                  handleInputChange("paymentType", e.target.value)
                }
                required
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
              {errors.paymentType && (
                <div className="invalid-feedback">{errors.paymentType}</div>
              )}
            </div>

            {/* Coupon Section - Only for Cash payments */}
            {showCouponSection && (
              <>
                <div className="mb-3">
                  <label htmlFor="couponCode" className="form-label">
                    Coupon Code (Optional)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="couponCode"
                      value={formData.couponCode}
                      onChange={(e) =>
                        handleInputChange("couponCode", e.target.value)
                      }
                      placeholder="Enter coupon code"
                      disabled={isCouponApplied}
                      ref={couponInputRef}
                    />
                    {!isCouponApplied ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleApplyCoupon}
                      >
                        Apply
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleRemoveCoupon}
                      >
                        Cancle
                      </button>
                    )}
                  </div>
                </div>

                {isCouponApplied && (
                  <div className="mb-3">
                    <strong>Final Amount: ₹{formData.finalAmount}</strong>
                  </div>
                )}
                {/* <input
                    type="number"
                    className={`form-control ${
                      errors.amount ? "is-invalid" : ""
                    }`}
                    id="amount"
                    value={formData.finalAmount}
                    // onChange={(e) =>
                    //   handleInputChange("amount", e.target.value)
                    // }
                    placeholder="Enter amount"
                    required
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">{errors.amount}</div>
                  )} */}
              </>
            )}

            {/* Additional Fields */}
            {additionalFields.map((field) => (
              <div key={field.name} className="mb-3">
                <label htmlFor={field.name} className="form-label">
                  {field.label}
                  {field.required && <span className="text-danger">*</span>}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    className={`form-control ${
                      errors[field.name] ? "is-invalid" : ""
                    }`}
                    id={field.name}
                    value={formData.additionalFieldValues[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange("additionalFieldValues", {
                        ...formData.additionalFieldValues,
                        [field.name]: e.target.value,
                      })
                    }
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                ) : (
                  <input
                    type={field.type || "text"}
                    className={`form-control ${
                      errors[field.name] ? "is-invalid" : ""
                    }`}
                    id={field.name}
                    value={formData.additionalFieldValues[field.name] || ""}
                    onChange={(e) =>
                      handleInputChange("additionalFieldValues", {
                        ...formData.additionalFieldValues,
                        [field.name]: e.target.value,
                      })
                    }
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
                {errors[field.name] && (
                  <div className="invalid-feedback">{errors[field.name]}</div>
                )}
              </div>
            ))}

            {/* Custom Error Display */}
            {errors.custom && (
              <div className="alert alert-danger">{errors.custom}</div>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Button variant="secondary" onClick={handleCancel}>
              {cancelButtonText}
            </Button>
            <div className="d-flex gap-2">
              <SendPaymentLinkButton
                patientId={paymentData?.patient_id}
                appointmentId={paymentData?.appointment_id}
                leadId={paymentData?.lead_id}
                prescriptionId={paymentData?.prescription_id}
                queueId={paymentData?.queue_id}
                paymentFor={paymentFor}
                patientName={patientName}
                amount={formData.amount}
                paymentType={paymentType}
                className="btn"
                size="sm"
                variant="outline-primary"
                style={{
                  backgroundColor: "#17a2b8",
                  borderColor: "#17a2b8",
                  color: "white",
                  fontWeight: "500",
                }}
                disabled={loading || isSubmitting}
              />
              <Button
                style={{
                  background: "#007680",
                  border: "none",
                }}
                onClick={handleSubmit}
                disabled={loading || isSubmitting}
              >
                {loading || isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  submitButtonText
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

CommonPaymentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  patientName: PropTypes.string,
  initialAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentModes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  enableCoupons: PropTypes.bool,
  couponCodes: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
  submitButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  showPatientName: PropTypes.bool,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  customValidation: PropTypes.func,
  additionalFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
    })
  ),
  onAmountChange: PropTypes.func,
  onPaymentTypeChange: PropTypes.func,
  onCouponChange: PropTypes.func,
  // New props
  paymentType: PropTypes.oneOf(["appointment", "medicine", "receptionist"]),
  paymentData: PropTypes.object,
  referenceId: PropTypes.string,
  paymentFor: PropTypes.string,
};

export default CommonPaymentModal;
